export const INTERACTION_STATES = Object.freeze({
  URGENT: "URGENT_ESCALATION",
  CLARIFY: "NEEDS_CLARIFICATION",
  READY: "READY_FOR_PLAN",
});

export const TURN_OPERATIONS = Object.freeze({
  ANSWER: "ANSWER",
  ADDENDUM: "ADDENDUM",
  CORRECTION: "CORRECTION",
  NEW_WOUND: "NEW_WOUND",
});

const ALLOWED_STATES = new Set(Object.values(INTERACTION_STATES));
const ALLOWED_URGENCY = new Set([
  "ED_NOW",
  "SAME_DAY",
  "URGENT_24_48_HOURS",
  "REVIEW_BEFORE_TREATMENT",
  "PROMPT_SYMPTOM_REVIEW",
  "ROUTINE_OR_DOCUMENTED",
  "NOT_APPLICABLE",
]);
const ALLOWED_OPERATIONS = new Set(Object.values(TURN_OPERATIONS));
const EXPECTED_CONVERSATION_MODE = Object.freeze({
  [INTERACTION_STATES.URGENT]: "EMERGENCY_DIRECTIVE",
  [INTERACTION_STATES.CLARIFY]: "TARGETED_CLARIFICATION",
  [INTERACTION_STATES.READY]: "PLAN_READY",
});
const QUESTION_ID_RE = /^[A-Za-z0-9_.:-]{1,100}$/;
const WOUND_ID_RE = /^[A-Za-z0-9_.:-]{1,64}$/;
const CORRECTION_PREFIX_RE = /^(?:correction\b|correcting\b|actually\b|i meant\b|amendment\b|entered in error\b|that was wrong\b)/i;
const NEW_WOUND_PREFIX_RE = /^(?:new wound\b|another wound\b|second wound\b|additional wound\b|separate wound\b)/i;

function objectRecord(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}

function boundedStrings(value, limit = 50) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item) => typeof item === "string" && item.trim())
    .slice(0, limit)
    .map((item) => item.trim());
}

function boundedFacts(value, limit = 100) {
  if (!Array.isArray(value)) return [];
  return value
    .slice(0, limit)
    .map((item) => {
      const fact = objectRecord(item);
      if (!fact || typeof fact.fact_id !== "string" || !fact.fact_id.trim()) return null;
      if (typeof fact.polarity !== "string" || typeof fact.temporality !== "string") return null;
      const output = {
        fact_id: fact.fact_id.trim(),
        polarity: fact.polarity.trim(),
        temporality: fact.temporality.trim(),
      };
      if (typeof fact.source === "string" && fact.source.trim()) output.source = fact.source.trim();
      if (typeof fact.wound_id === "string" && WOUND_ID_RE.test(fact.wound_id.trim())) {
        output.wound_id = fact.wound_id.trim();
      }
      if (typeof fact.value === "string" && fact.value.trim()) output.value = fact.value.trim();
      return output;
    })
    .filter(Boolean);
}

function normalizeOperation(value) {
  const operation = String(value || TURN_OPERATIONS.ANSWER).trim().toUpperCase();
  if (!ALLOWED_OPERATIONS.has(operation)) throw new Error("invalid clinical turn operation");
  return operation;
}

function normalizeWoundId(value, fallback = "wound-1") {
  const safeFallback = WOUND_ID_RE.test(String(fallback || "").trim())
    ? String(fallback).trim()
    : "wound-1";
  const woundId = String(value || safeFallback).trim();
  return WOUND_ID_RE.test(woundId) ? woundId : safeFallback;
}

function normalizePriorTurns(turns, fallbackWoundId = "wound-1") {
  const prior = Array.isArray(turns) ? turns.slice(0, 39) : [];
  const used = new Set();
  return prior
    .map((item, index) => {
      const row = objectRecord(item);
      if (!row || typeof row.answer !== "string" || !row.answer.trim()) return null;
      let turnId = typeof row.turn_id === "string" && QUESTION_ID_RE.test(row.turn_id)
        ? row.turn_id
        : `legacy-turn-${index + 1}`;
      let suffix = index + 1;
      while (used.has(turnId)) {
        suffix += 1;
        turnId = `legacy-turn-${suffix}`;
      }
      used.add(turnId);
      const questionId = typeof row.question_id === "string" && QUESTION_ID_RE.test(row.question_id)
        ? row.question_id
        : `followup.free_text.${index + 1}`;
      const operation = row.operation
        ? normalizeOperation(row.operation)
        : row.is_correction === true
          ? TURN_OPERATIONS.CORRECTION
          : TURN_OPERATIONS.ANSWER;
      const output = {
        ...row,
        turn_id: turnId,
        question_id: questionId,
        answer: row.answer.trim(),
        wound_id: normalizeWoundId(row.wound_id, fallbackWoundId),
        operation,
      };
      if (operation === TURN_OPERATIONS.CORRECTION) output.is_correction = true;
      else delete output.is_correction;
      if (typeof row.supersedes_turn_id === "string" && QUESTION_ID_RE.test(row.supersedes_turn_id)) {
        output.supersedes_turn_id = row.supersedes_turn_id;
      }
      if (typeof row.supersedes_question_id === "string" && QUESTION_ID_RE.test(row.supersedes_question_id)) {
        output.supersedes_question_id = row.supersedes_question_id;
      }
      return output;
    })
    .filter(Boolean);
}

function nextTurnId(turns) {
  const used = new Set((Array.isArray(turns) ? turns : []).map((item) => item?.turn_id).filter(Boolean));
  let ordinal = used.size + 1;
  while (used.has(`turn-${ordinal}`)) ordinal += 1;
  return `turn-${ordinal}`;
}

function nextWoundId(turns, questionWoundId = "wound-1") {
  const used = new Set([questionWoundId]);
  for (const item of Array.isArray(turns) ? turns : []) {
    if (typeof item?.wound_id === "string") used.add(item.wound_id);
  }
  let ordinal = 2;
  while (used.has(`wound-${ordinal}`)) ordinal += 1;
  return `wound-${ordinal}`;
}

function linkedQuestionId(pendingQuestions, ordinal, prior) {
  const questions = Array.isArray(pendingQuestions)
    ? pendingQuestions.filter((item) => QUESTION_ID_RE.test(String(item?.question_id || "")))
    : [];
  let candidate = questions.length === 1 ? questions[0].question_id
    : questions.length > 1 ? `followup.bundle.${ordinal}`
      : `followup.free_text.${ordinal}`;
  // Generated action ids can repeat in a later consultation. A new reply must
  // not silently supersede an earlier answer that happened to use the same id.
  const used = new Set(prior.map((turn) => turn.question_id));
  let suffix = ordinal;
  while (used.has(candidate)) candidate = `followup.reply.${suffix++}`;
  return candidate;
}

function normalizeConversation(value, state) {
  const row = objectRecord(value);
  if (!row) return null;
  const questions = Array.isArray(row.priority_questions)
    ? row.priority_questions
        .slice(0, 3)
        .map((item) => {
          const question = objectRecord(item);
          if (!question || !QUESTION_ID_RE.test(String(question.question_id || ""))) return null;
          if (typeof question.text !== "string" || !question.text.trim()) return null;
          return {
            question_id: question.question_id,
            domain: typeof question.domain === "string" ? question.domain : "other",
            text: question.text.trim(),
            priority: Number.isInteger(question.priority) ? question.priority : 0,
          };
        })
        .filter(Boolean)
    : [];
  const mode = typeof row.mode === "string" ? row.mode : null;
  if (!mode || mode !== EXPECTED_CONVERSATION_MODE[state]) return null;
  return {
    contract_version: typeof row.contract_version === "string" ? row.contract_version : null,
    implementation_version:
      typeof row.implementation_version === "string" ? row.implementation_version : null,
    mode,
    turn_count: Number.isInteger(row.turn_count) ? row.turn_count : 0,
    facts_locked_count: Number.isInteger(row.facts_locked_count) ? row.facts_locked_count : 0,
    priority_questions: questions,
    remaining_domains: boundedStrings(row.remaining_domains, 20),
    reply_prompt: typeof row.reply_prompt === "string" ? row.reply_prompt : null,
    corrections_supported: row.corrections_supported === true,
    state_transition_rule:
      typeof row.state_transition_rule === "string" ? row.state_transition_rule : null,
    urgency_while_clarifying:
      typeof row.urgency_while_clarifying === "string"
        ? row.urgency_while_clarifying
        : null,
  };
}

export function normalizeSupportiveCareScope(row) {
  if (!objectRecord(row)) return null;
  const scope = row.supportive_care_scope;
  if (scope === undefined) return row.urgency === "PROMPT_SYMPTOM_REVIEW" ? null : [];
  const domains = new Set(["pain_review", "nonmedicated_dressing", "tolerated_positioning"]);
  if (!Array.isArray(scope) || scope.length < 1 || scope.length > 3
      || new Set(scope).size !== scope.length || scope.some((v) => !domains.has(v))
      || row.state !== INTERACTION_STATES.CLARIFY || row.family !== "PRESSURE_INJURY"
      || row.treatment_plan_available !== false
      || !["PROMPT_SYMPTOM_REVIEW", "URGENT_24_48_HOURS", "ROUTINE_OR_DOCUMENTED"].includes(row.urgency)
      || !Array.isArray(row.contradictions) || row.contradictions.length !== 0
      || !Array.isArray(row.emergency_triggers) || row.emergency_triggers.length !== 0) return null;
  return [...scope];
}

export function normalizeClinicalInteraction(value) {
  const row = objectRecord(value);
  if (!row || !ALLOWED_STATES.has(row.state) || !ALLOWED_URGENCY.has(row.urgency)) {
    return null;
  }
  if (row.urgency === "REVIEW_BEFORE_TREATMENT" && row.state !== INTERACTION_STATES.CLARIFY) {
    return null;
  }
  if (
    typeof row.contract_version !== "string" ||
    typeof row.registry_version !== "string" ||
    typeof row.family !== "string" ||
    typeof row.phenotype !== "string" ||
    typeof row.working_impression !== "string" ||
    typeof row.treatment_plan_available !== "boolean"
  ) {
    return null;
  }
  const supportiveScope = normalizeSupportiveCareScope(row);
  if (supportiveScope === null) return null;
  const questions = Array.isArray(row.questions)
    ? row.questions
        .slice(0, 20)
        .map((item) => {
          const question = objectRecord(item);
          if (
            !question ||
            typeof question.question_id !== "string" ||
            !QUESTION_ID_RE.test(question.question_id) ||
            typeof question.domain !== "string" ||
            typeof question.text !== "string" ||
            !question.text.trim()
          ) {
            return null;
          }
          return {
            question_id: question.question_id,
            domain: question.domain,
            text: question.text.trim(),
            priority: Number.isInteger(question.priority) ? question.priority : 0,
          };
        })
        .filter(Boolean)
    : [];

  if (row.state === INTERACTION_STATES.READY && !row.treatment_plan_available) return null;
  if (row.state !== INTERACTION_STATES.READY && row.treatment_plan_available) return null;
  if (row.state === INTERACTION_STATES.URGENT && questions.length > 0) return null;

  const conversation = row.conversation == null
    ? null
    : normalizeConversation(row.conversation, row.state);
  if (row.conversation != null && !conversation) return null;

  return {
    contract_version: row.contract_version,
    registry_version: row.registry_version,
    state: row.state,
    urgency: row.urgency,
    ...(supportiveScope.length ? { supportive_care_scope: supportiveScope } : {}),
    family: row.family,
    phenotype: row.phenotype,
    working_impression: row.working_impression.trim(),
    known_facts: boundedFacts(row.known_facts),
    supplied_fact_summary: boundedStrings(row.supplied_fact_summary),
    unknown_facts: boundedStrings(row.unknown_facts),
    contradictions: boundedStrings(row.contradictions),
    questions,
    immediate_actions: boundedStrings(row.immediate_actions),
    emergency_triggers: boundedStrings(row.emergency_triggers),
    deferred_modalities: boundedStrings(row.deferred_modalities),
    treatment_plan_available: row.treatment_plan_available,
    reason_codes: boundedStrings(row.reason_codes),
    conversation,
  };
}

export function interactionIsBlocked(interaction) {
  return Boolean(
    interaction &&
      (interaction.state !== INTERACTION_STATES.READY ||
        !interaction.treatment_plan_available),
  );
}

export function buildGovernedRequest({
  caseAnchor,
  input,
  sessionId,
  turns,
  pendingQuestions = [],
  operation,
  supersedesTurnId,
  woundId,
  activeWoundId,
  questionWoundId = "wound-1",
  isCorrection = false,
  supersedesQuestionId,
}) {
  const normalizedInput = String(input || "").trim();
  if (!normalizedInput) throw new Error("clinical input is required");
  if (Array.isArray(turns) && turns.length >= 40) throw new Error("conversation_turn_limit");
  if (normalizedInput.length > 4000) throw new Error("conversation_size_limit");

  const original = caseAnchor || normalizedInput;
  const rawInitialWound = activeWoundId == null ? null : String(activeWoundId).trim();
  const includeInitialIdentity = rawInitialWound !== null && rawInitialWound !== "wound-1";
  const initialWound = normalizeWoundId(activeWoundId || questionWoundId || "wound-1");
  const normalizedQuestionWound = normalizeWoundId(
    caseAnchor ? questionWoundId : initialWound,
  );
  const prior = normalizePriorTurns(turns, normalizedQuestionWound);

  if (!caseAnchor) {
    const body = {
      question: original,
      session_id: sessionId,
    };
    if (includeInitialIdentity) {
      body.question_wound_id = initialWound;
      body.active_wound_id = initialWound;
    }
    return {
      body,
      clinicalTurns: prior,
      original,
      activeWoundId: initialWound,
      questionWoundId: initialWound,
    };
  }

  const inferredOperation = isCorrection || CORRECTION_PREFIX_RE.test(normalizedInput)
    ? TURN_OPERATIONS.CORRECTION
    : NEW_WOUND_PREFIX_RE.test(normalizedInput)
      ? TURN_OPERATIONS.NEW_WOUND
      : TURN_OPERATIONS.ANSWER;
  const resolvedOperation = operation
    ? normalizeOperation(operation)
    : inferredOperation;

  const inferredWound = resolvedOperation === TURN_OPERATIONS.NEW_WOUND
    ? nextWoundId(prior, normalizedQuestionWound)
    : activeWoundId || woundId || normalizedQuestionWound;
  const normalizedWound = normalizeWoundId(woundId || inferredWound, normalizedQuestionWound);
  const normalizedActiveWound = normalizeWoundId(
    resolvedOperation === TURN_OPERATIONS.NEW_WOUND
      ? normalizedWound
      : activeWoundId || normalizedWound,
    normalizedQuestionWound,
  );

  const legacyTargetQuestion = typeof supersedesQuestionId === "string"
    ? supersedesQuestionId.trim()
    : null;
  let target = supersedesTurnId ? String(supersedesTurnId).trim() : null;
  if (resolvedOperation === TURN_OPERATIONS.CORRECTION && !target && legacyTargetQuestion) {
    const matched = [...prior]
      .reverse()
      .find(
        (item) =>
          item.wound_id === normalizedWound &&
          item.question_id === legacyTargetQuestion,
      );
    if (!matched) throw new Error("correction target does not exist");
    target = matched.turn_id;
  }
  if (resolvedOperation !== TURN_OPERATIONS.CORRECTION && target) {
    throw new Error("only a correction may supersede a prior turn");
  }
  if (
    resolvedOperation === TURN_OPERATIONS.NEW_WOUND &&
    normalizedWound === normalizedQuestionWound
  ) {
    throw new Error("new wound requires a distinct wound id");
  }

  const ordinal = prior.length + 1;
  const turn = {
    turn_id: nextTurnId(prior),
    question_id: linkedQuestionId(pendingQuestions, ordinal, prior),
    answer: normalizedInput,
    wound_id: normalizedWound,
    operation: resolvedOperation,
  };
  if (resolvedOperation === TURN_OPERATIONS.ANSWER &&
      pendingQuestions.some((q) => typeof q.text === "string" && q.text.trim())) {
    const context = pendingQuestions.map(({ question_id, text }) => ({ question_id, text }));
    if (context.length > 3 || context.some((q) =>
      !QUESTION_ID_RE.test(String(q.question_id || "")) ||
      typeof q.text !== "string" || !q.text.trim() || q.text.length > 6000)) {
      throw new Error("conversation_question_limit");
    }
    turn.question_context = context;
  }
  if (resolvedOperation === TURN_OPERATIONS.CORRECTION) {
    turn.is_correction = true;
    if (!target) turn.infer_correction_target = true;
  }
  if (target) {
    turn.supersedes_turn_id = target;
    const targetTurn = prior.find((item) => item.turn_id === target);
    turn.supersedes_question_id =
      legacyTargetQuestion || targetTurn?.question_id || target;
  }

  const clinicalTurns = [...prior, turn];
  const body = {
    question: original,
    session_id: sessionId,
    question_wound_id: normalizedQuestionWound,
    active_wound_id: normalizedActiveWound,
    clinical_turns: clinicalTurns,
  };
  // Match the server's UTF-8 body limit; never discard clinical history to fit.
  if (new TextEncoder().encode(JSON.stringify(body)).length > 24576) {
    throw new Error("conversation_size_limit");
  }
  return {
    body,
    clinicalTurns,
    original,
    activeWoundId: normalizedActiveWound,
    questionWoundId: normalizedQuestionWound,
  };
}
